import React from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DashboardAnalytics, BrokerPerformance } from "@/types/dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { getUserDisplayName } from "@/utils/emailFormatter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AnalyticsChartsProps {
  analytics: DashboardAnalytics;
  period: number;
  isLoading: boolean;
}

const formatCurrency = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const APPROVAL_COLORS = {
  approved: "#2e7d32",
  declined: "#d32f2f",
  other: "#9e9e9e",
};

const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ analytics, period, isLoading }) => {
  const trendData = (analytics.decision_trend ?? []).map((point) => ({
    ...point,
    date:
      typeof point.date === "string"
        ? new Date(point.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : point.date,
  }));

  const pieData = [
    { name: "Approved", value: analytics.approved_count, color: APPROVAL_COLORS.approved },
    { name: "Declined", value: analytics.declined_count, color: APPROVAL_COLORS.declined },
  ].filter((d) => d.value > 0);

  const declineData = (analytics.top_decline_reasons ?? []).map((r) => ({
    reason: r.reason.length > 24 ? r.reason.slice(0, 22) + "..." : r.reason,
    fullReason: r.reason,
    count: r.count,
  }));

  const brokerPerf: BrokerPerformance[] = analytics.broker_performance ?? [];

  const approvalBarColor = (rate: number) =>
    rate >= 70 ? "#2e7d32" : rate >= 40 ? "#ed6c02" : "#d32f2f";

  return (
    <div>
      <div className="mb-6 grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-bold">
              Decision Trend
              <span className="ml-2 font-normal text-muted-foreground">Last {period} days</span>
            </h3>
            {isLoading ? (
              <Skeleton className="h-[280px] w-full rounded-md" />
            ) : trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradDeclined" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d32f2f" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#d32f2f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="approved"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#gradApproved)"
                    name="Approved"
                  />
                  <Area
                    type="monotone"
                    dataKey="declined"
                    stroke="#d32f2f"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#gradDeclined)"
                    name="Declined"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[280px] items-center justify-center">
                <p className="text-sm italic text-muted-foreground">
                  No trend data for this period
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-5">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-bold">Approval Breakdown</h3>
            {isLoading ? (
              <Skeleton className="mx-auto mt-4 h-[200px] w-[200px] rounded-full" />
            ) : pieData.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                      }}
                      formatter={(value: number, name: string) => [value, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 flex justify-center gap-8">
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">
                      {analytics.approval_percentage}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Approved ({analytics.approved_count})
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-red-600">
                      {analytics.decline_percentage}%
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Declined ({analytics.declined_count})
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-[280px] items-center justify-center">
                <p className="text-sm italic text-muted-foreground">No decision data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-bold">Top Decline Reasons</h3>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i}>
                    <Skeleton className="mb-1 h-5 w-3/5" />
                    <Skeleton className="h-2 w-full" />
                  </div>
                ))}
              </div>
            ) : declineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={declineData}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="reason" type="category" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    }}
                    formatter={(
                      value: number,
                      _: string,
                      props: { payload?: { fullReason?: string } }
                    ) => [value, props.payload?.fullReason ?? "Deals"]}
                  />
                  <Bar dataKey="count" fill="#d32f2f" radius={[0, 6, 6, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[200px] items-center justify-center">
                <p className="text-sm italic text-muted-foreground">No decline data</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex max-h-[400px] flex-col overflow-hidden lg:col-span-7">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-bold">Broker Performance</h3>
          </div>
          {isLoading ? (
            <div className="p-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="mb-2 h-12 w-full" />
              ))}
            </div>
          ) : brokerPerf.length > 0 ? (
            <ScrollArea className="max-h-[340px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Broker</TableHead>
                    <TableHead className="text-center">Deals</TableHead>
                    <TableHead className="text-center">Approval</TableHead>
                    <TableHead className="text-right">Volume</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brokerPerf.slice(0, 10).map((broker) => (
                    <TableRow key={broker.broker_id}>
                      <TableCell className="font-medium">
                        {getUserDisplayName(broker.broker_name)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="min-w-9 font-semibold">
                          {broker.total_submissions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(100, broker.approval_rate)}%`,
                                backgroundColor: approvalBarColor(broker.approval_rate),
                              }}
                            />
                          </div>
                          <span className="min-w-8 text-xs font-semibold">
                            {broker.approval_rate}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(broker.volume)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="p-6 text-center">
              <p className="text-sm italic text-muted-foreground">No broker data for this period</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsCharts;
